import React, { useContext, useMemo, useEffect, useRef, useState } from 'react';
import { MapFeatures, MapContextType } from '@sw-sw/common';
import { Context as DataSourceContext } from './DataSourceContext';
import { getUtil } from '../../utils/positionable';
import { useCalibrateMap } from './CalibrateMap/CalibrateMap';
import { MapEditorRouteContext } from '../CustomRoutes/MapEditor';
import { Modal } from '@sw-sw/lib-ui';

export const safeParseConfig = (config) => {
  if (!config) return null;

  if (typeof config === 'object') return config;

  if (typeof config === 'string') {
    try {
      return JSON.parse(config);
    } catch {
      return null;
    }
  }

  return null;
};

/**
 * Apply calibration transformation to position coordinates
 * Transforms relative to map center
 *
 * @param {number} x - Original x coordinate
 * @param {number} y - Original y coordinate
 * @param {number} scale - Scale adjustment from -50 to +50 (0 = no change)
 * @param {number} shiftX - Horizontal shift percentage from -20 to +20
 * @param {number} shiftY - Vertical shift percentage from -20 to +20
 * @param {object} mapExtent - Map extent {minX, minY, maxX, maxY}
 */
const applyCalibrateTransform = (
  x,
  y,
  pos_config,
  scale,
  shiftX,
  shiftY,
  mapExtent,
) => {
  const config = safeParseConfig(pos_config);

  const centerX = (mapExtent.maxX + mapExtent.minX) / 2;
  const centerY = (mapExtent.maxY + mapExtent.minY) / 2;

  // Calculate map dimensions
  const mapWidth = mapExtent.maxX - mapExtent.minX;
  const mapHeight = mapExtent.maxY - mapExtent.minY;

  const scaleFactor = 1 + scale / 100;

  const scaledX = centerX + (x - centerX) * scaleFactor;
  const scaledY = centerY + (y - centerY) * scaleFactor;

  // Apply shift (convert percentage to map units)
  const shiftXUnits = (shiftX / 100) * mapWidth;
  const shiftYUnits = (shiftY / 100) * mapHeight;

  if (config?.radius !== undefined) {
    if (config.radius === 45) {
      config.radius = config.radius;
    } else {
      config.radius *= scaleFactor;
    }
  } else if (config?.coordinates !== undefined) {
    if (config.coordinates.length < 2) {
      const configCoordinatesArr = config.coordinates[0];
      configCoordinatesArr.forEach((coord, index) => {
        const coordX = coord[0];
        const coordY = coord[1];
        const scaledCoordX =
          centerX + (coordX - centerX) * scaleFactor + shiftXUnits;
        const scaledCoordY =
          centerY + (coordY - centerY) * scaleFactor + shiftYUnits;
        configCoordinatesArr[index] = [scaledCoordX, scaledCoordY];
      });
    } else {
      config.coordinates.forEach((coord, index) => {
        const coordX = coord[0];
        const coordY = coord[1];
        const scaledCoordX =
          centerX + (coordX - centerX) * scaleFactor + shiftXUnits;
        const scaledCoordY =
          centerY + (coordY - centerY) * scaleFactor + shiftYUnits;
        config.coordinates[index] = [scaledCoordX, scaledCoordY];
      });
    }
  }

  return {
    x: scaledX + shiftXUnits,
    y: scaledY + shiftYUnits,
    pos_config: config ? JSON.stringify(config) : null,
  };
};

/**
 * Get map extent from OpenLayers map
 * This gets the actual coordinate extent of the map image
 */
const getMapExtent = (map) => {
  if (!map) {
    return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
  }

  try {
    // Get the view and its projection
    const view = map.getView();
    const projection = view.getProjection();

    // Try to get extent from projection first
    let extent = projection.getExtent();

    // If projection extent is not valid, try to get from image layer
    if (!extent || extent.some((val) => !isFinite(val))) {
      const layers = map.getLayers().getArray();

      // Find the image layer (usually the first layer)
      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        const source = layer.getSource();

        // Check if it's an image source with extent
        if (source && typeof source.getImageExtent === 'function') {
          extent = source.getImageExtent();
          break;
        }

        // Check for static image source
        if (
          source &&
          source.getImage &&
          typeof source.getImage === 'function'
        ) {
          const image = source.getImage();
          if (image && image.getExtent) {
            extent = image.getExtent();
            break;
          }
        }
      }
    }

    // If still no extent, calculate from view
    if (!extent || extent.some((val) => !isFinite(val))) {
      extent = view.calculateExtent(map.getSize());
    }

    return {
      minX: extent[0],
      minY: extent[1],
      maxX: extent[2],
      maxY: extent[3],
    };
  } catch (e) {
    console.warn('Error getting map extent:', e);
    return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
  }
};

/**
 * Render map features (positionable instances) from {DataSourceContext}
 */
export function DataSourceFeatures(props) {
  // All hooks must be called before any conditional returns
  const dataSource = useContext(DataSourceContext);
  const mapContext = useContext(MapContextType);
  const { scale, shiftX, shiftY, calibratedDataGroupsRef, setNeedsReupload } =
    useCalibrateMap();
  const renderKeyRef = useRef(0);
  const mapEditorRouteContext = useContext(MapEditorRouteContext);
  const [showCalibrationModal, setShowCalibrationModal] = useState(false);
  const [hasCheckedCalibration, setHasCheckedCalibration] = useState(false);

  // Get map extent for calibration
  const mapExtent = useMemo(() => {
    if (!mapContext || !mapContext.map) {
      return { minX: 0, minY: 0, maxX: 1000, maxY: 1000 };
    }
    return getMapExtent(mapContext.map);
  }, [mapContext]);

  // Track calibration changes and increment render key
  useEffect(() => {
    renderKeyRef.current += 1;
  }, [scale, shiftX, shiftY]);

  // Get data groups
  const dataGroups = useMemo(() => {
    if (!dataSource) return {};
    return dataSource.getDataInstanceGroups();
  }, [dataSource]);

  // Check if any data groups have null x_pct or y_pct
  const hasNullPercentages = useMemo(() => {
    if (!dataGroups || Object.keys(dataGroups).length === 0) return false;

    return Object.keys(dataGroups).some((groupName) => {
      const instances = dataGroups[groupName];
      return instances.some((instance) => {
        // Check if x_pct or y_pct is null
        return (
          (instance.x_pct === null || instance.y_pct === null) &&
          !instance.marked_deleted
        );
      });
    });
  }, [dataGroups]);

  // Show modal when component mounts if there are null percentages
  useEffect(() => {
    if (
      !hasCheckedCalibration &&
      hasNullPercentages &&
      mapContext &&
      mapContext.map &&
      !mapEditorRouteContext.mapMobileView
    ) {
      setShowCalibrationModal(true);
      setHasCheckedCalibration(true);
      // Set flag that reupload is needed
      if (setNeedsReupload) {
        setNeedsReupload(true);
      }
    }
  }, [
    hasNullPercentages,
    hasCheckedCalibration,
    mapContext,
    setNeedsReupload,
    mapEditorRouteContext.mapMobileView,
  ]);

  // Handle modal confirmation
  const handleCalibrationModalConfirm = () => {
    setShowCalibrationModal(false);
    // Show the calibrate map section
    if (mapEditorRouteContext.setShowCalibrateMap) {
      mapEditorRouteContext.setShowCalibrateMap(true);
    }
  };

  // Apply calibration to instances
  const calibratedDataGroups = useMemo(() => {
    const result = {};

    Object.keys(dataGroups).forEach((groupName) => {
      const instances = dataGroups[groupName];

      // If no calibration, use original instances
      if (scale === 0 && shiftX === 0 && shiftY === 0) {
        result[groupName] = instances;
      } else {
        // Apply calibration transformation
        result[groupName] = instances.map((instance) => {
          if (instance.marked_deleted) return instance;
          const transformed = applyCalibrateTransform(
            instance.position_x,
            instance.position_y,
            instance.positionable_config,
            scale,
            shiftX,
            shiftY,
            mapExtent,
          );

          return {
            ...instance,
            position_x: transformed.x,
            position_y: transformed.y,
            positionable_config: transformed.pos_config,
          };
        });
      }
    });

    return result;
  }, [dataGroups, scale, shiftX, shiftY, mapExtent]);

  // Update the ref with the latest calibratedDataGroups
  useEffect(() => {
    if (calibratedDataGroupsRef) {
      calibratedDataGroupsRef.current = calibratedDataGroups;
    }
  }, [calibratedDataGroups, calibratedDataGroupsRef]);

  // Create a unique key that changes when calibration changes
  const calibrationKey = `${scale}-${shiftX}-${shiftY}`;

  // Early return AFTER all hooks have been called
  if (!mapContext || !dataSource || !mapContext.map) {
    return null;
  }

  return (
    <React.Fragment>
      {Object.keys(calibratedDataGroups).map((groupName) => {
        return (
          <MapFeatures
            key={`${groupName}-${calibrationKey}`}
            positionableInstances={calibratedDataGroups[groupName]}
            getUtil={(...a) => {
              return getUtil(...a);
            }}
            getSourceModel={(id, positionableType) => {
              return dataSource.getById(id, positionableType);
            }}
            getLayer={mapContext.getLayer}
            showFeatureLabels={false}
            selectedInspection={props.selectedInspection}
          />
        );
      })}

      <Modal
        show={showCalibrationModal}
        handleSubmit={handleCalibrationModalConfirm}
        handleClose={() => setShowCalibrationModal(false)}
        title='Map Optimization Available'
        cancelBtn={true}
        cancelBtnText='Cancel'
        submitBtnText='Continue'
      >
        <>
          <p>
            An update is available to optimize this map for better display
            across devices.
          </p>
          <p>
            This update improves legend placement so it adapts consistently on
            different screen sizes.
          </p>
          <p>
            Please review the map to confirm that all legend items and findings
            appear correctly. You can adjust the scale and positioning of legend
            items before confirming the changes.
          </p>
        </>
      </Modal>
    </React.Fragment>
  );
}

export default DataSourceFeatures;
